/**
 * PestleListPage — PESTLE Assessment (Political, Economic, Social, Technological, Legal, Environmental)
 * Landing page: lists past assessments and starts new ones.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pestleApi, threatApi, threatRegistersApi, linkedArtefactsApi, registerConfigApi, templateApi } from '../services/api';
import { useSwal } from '../hooks/useSwal';
import { useToast } from '../hooks/useToast';
import { NimbusTable } from '../components/NimbusTable';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { PageHeader } from '../components/PageHeader';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTh } from '../components/SortableTh';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import type { Tone } from '../constants/tone';

type Assessment = {
  pestleId: string;
  title: string;
  description?: string;
  status: 'draft' | 'complete';
  factorCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
};

type SortField = 'title' | 'status' | 'factorCount' | 'updatedAt';

export const PestleListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const swal = useSwal();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [migratingAssessment, setMigratingAssessment] = useState<Assessment | null>(null);
  const [migrateRegisterId, setMigrateRegisterId] = useState('');
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [showNewRegisterForm, setShowNewRegisterForm] = useState(false);
  const [newRegisterName, setNewRegisterName] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['pestle-assessments'],
    queryFn: () => pestleApi.list(),
  });

  const { data: settingsData } = useQuery({
    queryKey: ['pestle-settings'],
    queryFn: () => pestleApi.getSettings(),
  });

  // Initialise local date state from loaded settings
  useEffect(() => {
    if (settingsData?.settings?.nextReviewDate) {
      setReviewDate(settingsData.settings.nextReviewDate);
    }
  }, [settingsData]);

  const saveSettingsMutation = useMutation({
    mutationFn: (date: string) => pestleApi.updateSettings({ nextReviewDate: date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pestle-settings'] });
      queryClient.invalidateQueries({ queryKey: ['pestle-all-maint'] });
      toast.success('Review date saved');
    },
    onError: () => toast.error('Failed to save review date'),
  });

  const createMutation = useMutation({
    mutationFn: (title: string) => pestleApi.create({ title }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['pestle-assessments'] });
      const id = result.assessment?.pestleId;
      if (id) {
        window.location.href = `/pestle/${id}`;
      }
    },
    onError: () => toast.error('Failed to create assessment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pestleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pestle-assessments'] });
      toast.success('Assessment deleted');
    },
    onError: () => toast.error('Failed to delete assessment'),
  });

  const handleCreate = () => {
    const title = newTitle.trim() || `PESTLE Assessment — ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    setCreating(false);
    setNewTitle('');
    createMutation.mutate(title);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await swal.confirmDelete('this assessment');
    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  const { data: threatRegisters = [], refetch: refetchThreatRegisters } = useQuery({
    queryKey: ['threat-registers'],
    queryFn: threatRegistersApi.getAll,
  });

  const handleCreateRegister = async () => {
    if (!newRegisterName.trim()) return;
    try {
      const result = await threatRegistersApi.create({ name: newRegisterName.trim() });
      const newId = result?.registerId || result?.register?.registerId;
      if (newId) {
        try {
          const templates = await templateApi.list('threat');
          const standard = templates.find((t: any) => t.templateName?.toLowerCase() === 'standard') ?? templates[0];
          if (standard) await registerConfigApi.applyTemplate('threat', newId, standard.templateName);
        } catch { /* template apply is best-effort */ }
      }
      await refetchThreatRegisters();
      if (newId) setMigrateRegisterId(newId);
      setShowNewRegisterForm(false);
      setNewRegisterName('');
      toast.success('Threat register created.');
    } catch {
      toast.error('Failed to create threat register.');
    }
  };

  const handleMigrateAll = async () => {
    if (!migratingAssessment || !migrateRegisterId) return;
    setMigrateLoading(true);
    try {
      const full = await pestleApi.get(migratingAssessment.pestleId);
      const allFactors: any[] = full?.assessment?.factors || [];
      const factors = allFactors.filter((f: any) => f.type === 'threat');
      if (factors.length === 0) { toast.warning('No threat factors to migrate.'); setMigrateLoading(false); return; }
      let created = 0, failed = 0;
      for (const f of factors) {
        try {
          const result = await threatApi.create({
            registerId: migrateRegisterId,
            status: 'active',
            fields: {
              threat_name: f.title,
              threat_description: f.description || '',
              threat_notes: f.notes || '',
              threat_priority: f.impact,
            },
          });
          const threatId = result?.threatId || result?.threat?.threatId;
          if (threatId && (f.linkedRisks || []).length > 0) {
            await linkedArtefactsApi.createLinks({
              source_id: threatId,
              source_type: 'threat',
              source_register: migrateRegisterId,
              target_artefacts: f.linkedRisks.map((lr: any) => ({
                id: lr.riskId,
                type: 'risk',
                register: lr.registerId || '',
              })),
            });
          }
          created++;
        } catch { failed++; }
      }
      if (failed === 0) {
        toast.success(`${created} threat${created !== 1 ? 's' : ''} created in threat register.`);
      } else {
        toast.warning(`${created} created, ${failed} failed.`);
      }
      setMigratingAssessment(null);
      setMigrateRegisterId('');
    } catch (err) {
      console.error(err);
      toast.error('Migration failed.');
    } finally {
      setMigrateLoading(false);
    }
  };

  const assessments: Assessment[] = data?.assessments || [];
  const completed = assessments.filter(a => a.status === 'complete').length;
  const inProgress = assessments.filter(a => a.status === 'draft').length;

  const { sorted, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<Assessment, SortField>(
    assessments,
    (a, f) => (f === 'factorCount' ? a.factorCount : String(a[f] ?? '')),
    'title',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(sorted, 25);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-mind-map"
        title="PESTLE Assessment"
        subtitle="Analyse Political, Economic, Social, Technological, Legal, and Environmental factors affecting the organisation."
        colour="info"
        actions={
          <>
            <button
              className="np-btn np-btn-primary"
              onClick={() => setCreating(true)}
              disabled={createMutation.isPending}
            >
              <i className="ri-add-line"></i>
              {createMutation.isPending ? 'Creating…' : 'New Assessment'}
            </button>
          </>
        }
      />

      {/* Assessment List */}
      <div className="np-card">
        {/* Card header: title + stats + review date */}
        <div className="np-card-head flex-wrap">
          <div className="d-flex align-items-center gap-2 me-auto">
            <i className="ri-list-check-2 text-primary"></i>
            <h2>Assessments</h2>
            <span className="np-chip np-tone-neutral">{assessments.length}</span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {completed > 0 && (
              <span className="np-chip np-tone-success">
                <i className="ri-checkbox-circle-line"></i>{completed} completed
              </span>
            )}
            {inProgress > 0 && (
              <span className="np-chip np-tone-warning">
                <i className="ri-time-line"></i>{inProgress} in progress
              </span>
            )}
            <div className="vr mx-1 d-none d-sm-block" style={{ opacity: 0.2 }} />
            <div className="d-flex align-items-center gap-2">
              <i className={`ri-calendar-check-line small ${reviewDate && new Date(reviewDate) < new Date() ? 'text-danger' : 'np-sub'}`}></i>
              <span className="np-sub small" style={{ whiteSpace: 'nowrap' }}>Next review:</span>
              <input
                type="date"
                className="np-input border-0 bg-transparent p-0"
                style={{ width: 130, fontSize: '0.8125rem' }}
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                onBlur={() => { if (reviewDate !== (settingsData?.settings?.nextReviewDate || '')) saveSettingsMutation.mutate(reviewDate); }}
              />
              {reviewDate && new Date(reviewDate) < new Date() && (
                <span className="np-chip np-tone-danger"><i className="ri-alarm-warning-line"></i>Overdue</span>
              )}
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="np-card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="np-card-body">
            <div className="np-banner np-tone-danger mb-0">
              <i className="ri-error-warning-line"></i>
              Failed to load assessments.
            </div>
          </div>
        )}

        {!isLoading && !error && assessments.length === 0 && !creating && (
          <div className="np-card-body text-center py-5">
            <span className="np-icon-badge-lg np-tone-info mb-3 mx-auto">
              <i className="ri-mind-map"></i>
            </span>
            <h5 className="mb-1">Start your first PESTLE assessment</h5>
            <p className="np-sub mb-3">Analyse external factors: Political, Economic, Social, Technological, Legal, and Environmental.</p>
            <button className="np-btn np-btn-primary" onClick={() => setCreating(true)}>
              <i className="ri-add-line"></i>New Assessment
            </button>
          </div>
        )}

        {!isLoading && (assessments.length > 0 || creating) && (
          <NimbusTable thead={<tr>
              <SortableTh field="title" active={sortField === 'title'} dir={sortDir} onSort={toggleSort}>Assessment</SortableTh>
              <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={toggleSort} width="160px">Status</SortableTh>
              <SortableTh field="factorCount" active={sortField === 'factorCount'} dir={sortDir} onSort={toggleSort} width="120px">Factors</SortableTh>
              <SortableTh field="updatedAt" active={sortField === 'updatedAt'} dir={sortDir} onSort={toggleSort} width="160px">Last Modified</SortableTh>
              <th></th>
            </tr>}>
                {/* Inline new assessment row */}
                {creating && (
                  <tr>
                    <td colSpan={5}>
                      <div className="d-flex align-items-center gap-2 py-1">
                        <span className="np-icon-badge-sm np-tone-primary flex-shrink-0">
                          <i className="ri-add-line"></i>
                        </span>
                        <input
                          type="text"
                          className="np-input"
                          placeholder={`PESTLE Assessment — ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                          autoFocus
                        />
                        <button className="np-btn np-btn-sm np-btn-primary" onClick={handleCreate}>
                          <i className="ri-play-line"></i>Start
                        </button>
                        <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => { setCreating(false); setNewTitle(''); }}>
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {pageItems.map((a) => {
                  const isComplete = a.status === 'complete';
                  const tone: Tone = isComplete ? 'success' : 'warning';
                  const date = new Date(a.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

                  return (
                    <tr key={a.pestleId}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`np-icon-badge-sm np-tone-${tone}`}>
                            <i className="ri-mind-map"></i>
                          </span>
                          <div>
                            <div className="fw-semibold">{a.title}</div>
                            {a.description && <div className="np-sub small">{a.description.substring(0, 50)}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        {isComplete
                          ? <span className={`np-chip np-tone-${tone}`}><i className="ri-checkbox-circle-line"></i>Completed</span>
                          : <span className={`np-chip np-tone-${tone}`}><i className="ri-time-line"></i>In Progress</span>}
                      </td>
                      <td>
                        <span className="fw-semibold np-tabular">{a.factorCount}</span>
                        <span className="np-sub fw-normal small"> factor{a.factorCount !== 1 ? 's' : ''}</span>
                      </td>
                      <td className="np-sub small np-tabular">{date}</td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <a href={`/pestle/${a.pestleId}`} className="np-btn np-btn-sm np-btn-primary">
                            <i className="ri-edit-line"></i>Edit
                          </a>
                          <button
                            className="np-icon-btn np-icon-btn-success"
                            title="Migrate all factors to Threat Register"
                            onClick={() => { setMigratingAssessment(a); setMigrateRegisterId((threatRegisters as any[])[0]?.registerId || ''); }}
                          >
                            <i className="ri-arrow-right-up-line"></i>
                          </button>
                          <button
                            className="np-icon-btn np-icon-btn-danger"
                            onClick={() => handleDelete(a.pestleId)}
                            disabled={deleteMutation.isPending}
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </NimbusTable>
        )}
        {pageItems.length > 0 && (
          <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
        )}
      </div>

      {/* Bulk migrate modal */}
      {migratingAssessment && (
        <NimbusModal centered>
          <NimbusModalContent
            title={<><i className="ri-arrow-right-up-line me-2"></i>Migrate Assessment to Threat Register</>}
            onClose={() => { setMigratingAssessment(null); setShowNewRegisterForm(false); setNewRegisterName(''); }}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={() => { setMigratingAssessment(null); setShowNewRegisterForm(false); setNewRegisterName(''); }}>Cancel</button>
                <button
                  type="button"
                  className="np-btn np-btn-success"
                  onClick={handleMigrateAll}
                  disabled={migrateLoading || !migrateRegisterId || (threatRegisters as any[]).length === 0}
                >
                  {migrateLoading
                    ? <><span className="spinner-border spinner-border-sm" role="status" />Migrating…</>
                    : <><i className="ri-arrow-right-up-line"></i>Migrate All Factors</>}
                </button>
              </>
            }
          >
            <p className="np-sub small mb-3">
              This will create one threat per <strong>threat-type factor</strong> in <strong>{migratingAssessment.title}</strong>. Opportunity factors will be skipped. Each migrated factor's linked risks will be carried across into the new threats' Linked Artefacts sections.
            </p>
            <div className="np-field">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <label className="mb-0">Target Threat Register <span className="text-danger">*</span></label>
                <button type="button" className="np-btn np-btn-sm np-btn-ghost py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => setShowNewRegisterForm(v => !v)}>
                  <i className="ri-add-line"></i>New Register
                </button>
              </div>
              {showNewRegisterForm && (
                <div className="border rounded p-2 mb-2" style={{ background: 'var(--bs-body-bg)' }}>
                  <div className="d-flex gap-2">
                    <input
                      className="np-input"
                      placeholder="Register name"
                      value={newRegisterName}
                      onChange={e => setNewRegisterName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreateRegister()}
                      autoFocus
                    />
                    <button type="button" className="np-btn np-btn-sm np-btn-primary" onClick={handleCreateRegister} disabled={!newRegisterName.trim()}>Create</button>
                    <button type="button" className="np-btn np-btn-sm np-btn-ghost" onClick={() => { setShowNewRegisterForm(false); setNewRegisterName(''); }}>Cancel</button>
                  </div>
                </div>
              )}
              {(threatRegisters as any[]).length === 0 ? (
                <div className="np-banner np-tone-warning py-2 small"><i className="ri-error-warning-line"></i>No threat registers found. Use "New Register" above to create one.</div>
              ) : (
                <select
                  className="np-select"
                  value={migrateRegisterId}
                  onChange={e => setMigrateRegisterId(e.target.value)}
                >
                  {(threatRegisters as any[]).map((r: any) => (
                    <option key={r.registerId} value={r.registerId}>{r.name || r.registerId}</option>
                  ))}
                </select>
              )}
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
